/**
 * Device Detection Utilities
 * 
 * Provides functions to detect device type for adaptive UI behavior.
 */

/**
 * Detects if the current device is a mobile phone or tablet.
 * Used to determine whether to use native camera capture or custom modal.
 * 
 * @returns true if mobile/tablet, false if desktop
 */
export const isMobileOrTablet = (): boolean => {
  // Check standard mobile user agents
  const mobileRegex = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(navigator.userAgent);
  
  // Check for iPad masquerading as Mac (iPadOS 13+)
  // iPads report as "MacIntel" but have touch support
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  
  // Consider it mobile/tablet if:
  // 1. Mobile user agent detected, OR
  // 2. iPad pretending to be Mac
  return isMobileUA || isIPadOS;
};

/**
 * Checks if the device has camera capabilities.
 * 
 * @returns Promise<boolean> true if camera is available
 */
export const hasCamera = async (): Promise<boolean> => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return false;
    }
    
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(device => device.kind === 'videoinput');
  } catch {
    return false;
  }
};

/**
 * Checks if multiple cameras are available (for flip functionality).
 * 
 * @returns Promise<boolean> true if more than one camera exists
 */
export const hasMultipleCameras = async (): Promise<boolean> => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return false;
    }
    
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter(device => device.kind === 'videoinput');
    return cameras.length > 1;
  } catch {
    return false;
  }
};
